import { useMemo, useState, useEffect } from 'react';

export interface PaginationState<T> {
  page: number;
  pageSize: number;
  totalPages: number;
  total: number;
  items: T[];
  setPage: (p: number) => void;
  setPageSize: (n: number) => void;
  canPrev: boolean;
  canNext: boolean;
}

/**
 * Client-side pagination over an in-memory array.
 * When filters change (array shrinks), page snaps back to 1 automatically.
 */
export function usePagination<T>(items: T[], initialPageSize = 25): PaginationState<T> {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Snap back if current page is now out of range (filter shrunk the list)
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return {
    page,
    pageSize,
    totalPages,
    total,
    items: paged,
    setPage,
    setPageSize,
    canPrev: page > 1,
    canNext: page < totalPages,
  };
}
